import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { authThrowSchema } from '../schema/errorDemoSchema';
import type { AuthThrowFormValues } from '../schema/errorDemoSchema';
import type { ErrorCodeNode } from '../types/errorDemo';
import { useAuthThrow } from '../hooks/useThrowDemo';
import { ThrowResultCard } from './ThrowResultCard';

interface AuthThrowPanelProps {
  selectedNode: ErrorCodeNode | null;
}

/**
 * Panel for POST /api/eraf/error-demo/simulate/auth (R8-E-001: was /throw/auth).
 * 401 variant does NOT trigger logout (whitelist).
 */
export function AuthThrowPanel({ selectedNode }: AuthThrowPanelProps) {
  const { t } = useTranslation();
  const { result, throwAuth, reset } = useAuthThrow();

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<AuthThrowFormValues>({
    resolver: zodResolver(authThrowSchema),
    defaultValues: {
      variant: selectedNode?.code.includes('401') ? '401' : '403',
    },
  });

  const variant = watch('variant');

  // Sync when node changes
  useEffect(() => {
    if (selectedNode) {
      const v = selectedNode.code.includes('401') ? '401' : '403';
      setValue('variant', v);
      reset();
    }
  }, [selectedNode, setValue, reset]);

  const onSubmit = async (values: AuthThrowFormValues) => {
    await throwAuth({ variant: values.variant });
  };

  return (
    <div
      style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-4)' }}
      aria-label={t('errorDemo.auth.title')}
    >
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
        <span
          style={{
            fontSize: 'var(--font-size-base)',
            fontWeight: 700,
            color: 'var(--color-text-primary)',
          }}
        >
          Auth throw
        </span>
        <span
          style={{
            fontSize: 'var(--font-size-xs)',
            padding: '2px var(--spacing-2)',
            borderRadius: 'var(--radius-full)',
            background: 'var(--color-surface-elevated)',
            border: '1px solid var(--color-surface-border)',
            color: 'var(--color-text-muted)',
            fontFamily: 'var(--font-family-mono)',
          }}
        >
          POST /simulate/auth
        </span>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit(onSubmit)} noValidate aria-label={t('errorDemo.auth.title')}>
        {/* Variant radio group */}
        <fieldset
          style={{
            border: `1px solid ${errors.variant ? 'var(--color-danger)' : 'var(--color-surface-border)'}`,
            borderRadius: 'var(--radius-sm)',
            padding: 'var(--spacing-3)',
            marginBottom: 'var(--spacing-4)',
          }}
        >
          <legend
            style={{
              fontSize: 'var(--font-size-sm)',
              fontWeight: 600,
              color: 'var(--color-text-secondary)',
              padding: '0 var(--spacing-2)',
            }}
          >
            variant
          </legend>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-2)' }}>
            {(['401', '403'] as const).map((v) => (
              <label
                key={v}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--spacing-2)',
                  cursor: 'pointer',
                  fontSize: 'var(--font-size-sm)',
                  color: variant === v ? 'var(--color-brand-primary)' : 'var(--color-text-primary)',
                }}
              >
                <input
                  type="radio"
                  value={v}
                  {...register('variant')}
                  aria-describedby={errors.variant ? 'auth-variant-error' : undefined}
                />
                <span style={{ fontFamily: 'var(--font-family-mono)' }}>
                  {v === '401' ? '401 UNAUTHORIZED' : '403 FORBIDDEN'}
                </span>
              </label>
            ))}
          </div>
          {errors.variant && (
            <span
              id="auth-variant-error"
              role="alert"
              style={{
                display: 'block',
                fontSize: 'var(--font-size-xs)',
                color: 'var(--color-danger)',
                marginTop: 'var(--spacing-1)',
              }}
            >
              {errors.variant.message}
            </span>
          )}
        </fieldset>

        {/* Submit */}
        <button
          type="submit"
          disabled={isSubmitting || result.status === 'loading'}
          aria-busy={isSubmitting || result.status === 'loading'}
          style={{
            padding: 'var(--spacing-2) var(--spacing-6)',
            background:
              isSubmitting || result.status === 'loading'
                ? 'var(--color-surface-elevated)'
                : 'var(--color-brand-primary)',
            color:
              isSubmitting || result.status === 'loading'
                ? 'var(--color-text-muted)'
                : 'var(--color-text-inverse)',
            border: 'none',
            borderRadius: 'var(--radius-sm)',
            fontSize: 'var(--font-size-sm)',
            fontWeight: 700,
            cursor: isSubmitting || result.status === 'loading' ? 'not-allowed' : 'pointer',
            fontFamily: 'var(--font-family-sans)',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 'var(--spacing-2)',
          }}
        >
          {(isSubmitting || result.status === 'loading') && (
            <span
              aria-hidden="true"
              style={{
                display: 'inline-block',
                width: 14,
                height: 14,
                border: '2px solid currentColor',
                borderTopColor: 'transparent',
                borderRadius: 'var(--radius-full)',
                animation: 'spin 0.75s linear infinite',
              }}
            />
          )}
          throw
        </button>
      </form>

      {/* Hint */}
      <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', fontStyle: 'italic' }}>
        {t('errorDemo.auth.hint')}
      </p>

      {/* Result */}
      <ThrowResultCard result={result} />
    </div>
  );
}
